import React from 'react';
import { CreditCard, Hotel, ArrowRight, Check } from 'lucide-react';
import { motion } from 'framer-motion';

export const ProduitsInnova = () => {
  return (
    <section id="produits" className="py-24 relative overflow-hidden">
      <div className="absolute inset-0 network-lines opacity-5"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#FF6B35] opacity-5 blur-[150px] rounded-full"></div>
      
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#FF6B35] mb-4 block">
            Nos Produits
          </span>
          <h2 className="text-4xl sm:text-5xl font-black mb-6 tracking-tight">
            Solutions<span className="text-[#FF6B35]"> Logicielles</span>
          </h2>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            Des logiciels innovants conçus pour optimiser vos opérations et votre rentabilité
          </p>
        </motion.div>

        {/* Products Grid */}
        <div className="grid lg:grid-cols-2 gap-8">
          
          {/* Innova-Pay */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="group relative bg-[#121212] border border-white/10 rounded-2xl overflow-hidden hover:border-[#FF6B35]/50 transition-all duration-300"
          >
            {/* Image Background */}
            <div className="relative h-64 overflow-hidden">
              <img 
                src="https://images.pexels.com/photos/4226273/pexels-photo-4226273.jpeg" 
                alt="Innova-Pay"
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#121212] via-[#121212]/50 to-transparent"></div>
              
              {/* Logo Badge */}
              <div className="absolute top-6 left-6">
                <div className="bg-[#FF6B35] text-white px-4 py-2 rounded-lg font-bold text-lg">
                  INNOVA<span className="text-black">·</span>PAY
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-8">
              <h3 className="text-3xl font-black mb-4 group-hover:text-[#FF6B35] transition-colors">
                Solutions de Paiement
              </h3>
              <p className="text-gray-400 mb-6 leading-relaxed">
                Plateforme de paiement complète et sécurisée pour votre entreprise. Acceptez tous types de paiements avec une solution moderne et fiable.
              </p>

              {/* Features */}
              <ul className="space-y-3 mb-8">
                {[
                  'Paiements par carte et mobile',
                  'Transactions sécurisées PCI-DSS',
                  'Tableau de bord en temps réel',
                  'Intégration API simple'
                ].map((feature, i) => (
                  <li key={i} className="flex items-center gap-3 text-sm">
                    <div className="w-5 h-5 bg-[#FF6B35]/20 rounded-full flex items-center justify-center">
                      <Check className="w-3 h-3 text-[#FF6B35]" />
                    </div>
                    <span className="text-gray-300">{feature}</span>
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <a 
                href="https://innova-pay.ca" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#FF6B35] text-white font-semibold px-6 py-3 rounded-lg hover:bg-[#FF8555] transition-all hover:shadow-[0_0_20px_rgba(255,107,53,0.4)] group"
              >
                Visiter Innova-Pay
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
          </motion.div>

          {/* Gestimotel */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="group relative bg-[#121212] border border-white/10 rounded-2xl overflow-hidden hover:border-[#FF6B35]/50 transition-all duration-300"
          >
            {/* Image Background */}
            <div className="relative h-64 overflow-hidden">
              <img 
                src="https://images.unsplash.com/photo-1720962158789-9389a4f399da" 
                alt="Gestimotel"
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#121212] via-[#121212]/50 to-transparent"></div>
              
              {/* Logo Badge */}
              <div className="absolute top-6 left-6">
                <div className="glass px-4 py-2 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Hotel className="w-5 h-5 text-[#FF6B35]" />
                    <span className="font-bold text-lg">Gestimotel</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-8">
              <h3 className="text-3xl font-black mb-4 group-hover:text-[#FF6B35] transition-colors">
                Gestion Hôtelière
              </h3>
              <p className="text-gray-400 mb-6 leading-relaxed">
                Logiciel complet de gestion pour hôtels et auberges. Simplifiez vos réservations, votre facturation et la gestion de vos chambres.
              </p>

              {/* Features */}
              <ul className="space-y-3 mb-8">
                {[
                  'Gestion des réservations en ligne',
                  'Facturation et comptabilité intégrées',
                  'Planning des chambres interactif',
                  'Rapports et statistiques détaillés'
                ].map((feature, i) => (
                  <li key={i} className="flex items-center gap-3 text-sm">
                    <div className="w-5 h-5 bg-[#FF6B35]/20 rounded-full flex items-center justify-center">
                      <Check className="w-3 h-3 text-[#FF6B35]" />
                    </div>
                    <span className="text-gray-300">{feature}</span>
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <button 
                onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                className="inline-flex items-center gap-2 bg-transparent border-2 border-[#FF6B35] text-[#FF6B35] font-semibold px-6 py-3 rounded-lg hover:bg-[#FF6B35] hover:text-white transition-all"
              >
                En savoir plus
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </motion.div>
        </div>

        {/* Support IT Banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-16 glass rounded-2xl p-8 md:p-12 border border-[#FF6B35]/30"
        >
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-3xl font-bold mb-4">Besoin d'assistance technique?</h3>
              <p className="text-gray-400 mb-6">
                Notre équipe de support est disponible 24/7 pour vous aider avec tous vos besoins informatiques
              </p>
              <div className="flex flex-wrap gap-4">
                <a 
                  href="/supportit"
                  className="bg-[#FF6B35] text-white font-semibold px-6 py-3 rounded-lg hover:bg-[#FF8555] transition-all hover:shadow-[0_0_20px_rgba(255,107,53,0.4)] inline-flex items-center gap-2"
                >
                  Créer un ticket
                  <ArrowRight className="w-4 h-4" />
                </a>
                <a 
                  href="tel:+14503599394"
                  className="bg-transparent border-2 border-white/20 text-white font-semibold px-6 py-3 rounded-lg hover:border-white transition-all"
                >
                  Appeler: (450) 359-9394
                </a>
              </div>
            </div>
            <div className="hidden md:flex justify-center">
              <div className="w-48 h-48 bg-[#FF6B35]/20 rounded-full flex items-center justify-center">
                <div className="w-32 h-32 bg-[#FF6B35]/30 rounded-full flex items-center justify-center">
                  <div className="w-20 h-20 bg-[#FF6B35] rounded-full flex items-center justify-center">
                    <span className="text-4xl font-black">24/7</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
